import React, {useMemo, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {dayWindow, fetchLogsInRange} from '@/entities/log/day';
import {isoDate} from '@/entities/attendance/period';
import {PersonAvatar} from '@/entities/personnel/ui/PersonAvatar';
import {
  buildPresence,
  presenceCounts,
  type PresenceRow,
  type PresenceStatus,
} from '@/features/view-presence/lib/presence';
import {formatClock, formatCount} from '@/shared/lib/format';
import {Card} from '@/shared/ui/card';
import {useContentInsets} from '@/shared/ui/screen';
import {EmptyState} from '@/shared/ui/empty-state';
import {HelpNote} from '@/shared/ui/explain';
import {SectionHeader} from '@/shared/ui/section';
import {SkeletonList} from '@/shared/ui/skeleton';
import {StatusPill, type StatusTone} from '@/shared/ui/status';
import {AppInput} from '@/shared/ui/input';
import {FilterBar, FilterSelect} from '@/shared/ui/filter-bar';
import {useTableSort, type SortAccessors} from '@/shared/lib/table-sort';
import {useClientPage} from '@/shared/lib/use-client-page';
import {Pager} from '@/shared/ui/pager';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * «Кто сейчас на объекте» — сегодняшняя лента, свёрнутая до одной строки на человека.
 *
 * Вопрос владельца звучит не как «покажи журнал», а как «Ахмат ещё здесь?», и лента на
 * триста строк отвечает на него хуже, чем список на двадцать. Свёртка — в
 * `lib/presence.ts`, там же разобрано, почему статусов три и почему третий нельзя
 * убрать: дверь в режиме «оба» направления не сообщает.
 *
 * Считается по ЗАПРОШЕННЫМ У СЕРВЕРА разрешённым проходам (`result=granted`) за
 * сегодняшние сутки — сутки короткие, поэтому обход ленты здесь дёшев, в отличие от
 * произвольного периода в архиве. Не дошли до начала суток — говорим об этом: список,
 * оборвавшийся на середине дня, читается как «остальных не было».
 */
/** Строк на странице доски и что предлагает рейка под ней. */
const PAGE_SIZE = 25;
const PAGE_SIZES = [25, 50, 100] as const;

const styles = StyleSheet.create({
  root: {
    gap: spacing(4),
    paddingTop: spacing(4),
    paddingBottom: spacing(8),
    width: '100%',
    alignSelf: 'center',
  },
  counts: {flexDirection: 'row', gap: spacing(3), flexWrap: 'wrap'},
  count: {flex: 1, minWidth: 104, gap: 2},
  list: {gap: spacing(2)},

  row: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  rail: {width: 3, alignSelf: 'stretch', borderRadius: radii.pill},
  body: {flex: 1, gap: 2},
  meta: {alignItems: 'flex-end', gap: 2},
});

const TONE: Record<PresenceStatus, StatusTone> = {
  inside: 'granted',
  left: 'neutral',
  seen: 'pending',
};

function PresenceCard({
  companyId,
  row,
}: {
  companyId: string;
  row: PresenceRow;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const rail =
    row.status === 'inside' ? p.signal : row.status === 'seen' ? p.warning : p.border;

  return (
    <Card padding={3} testID={`presence-${row.recordId}`}>
      <View style={styles.row}>
        <View style={[styles.rail, {backgroundColor: rail}]} />
        <PersonAvatar
          companyId={companyId}
          recordId={row.recordId}
          name={row.name}
          size={40}
        />
        <View style={styles.body}>
          <AppText variant="subtitle" numberOfLines={1}>
            {row.name}
          </AppText>
          {/* «С 09:14» — про начало дня, а не про последнее событие: именно это
              спрашивают вторым вопросом, сразу после «здесь ли он». */}
          <AppText variant="micro" numberOfLines={1}>
            {t('presence.since', {time: formatClock(row.firstMillis)})}
            {' · '}
            {row.lastDeviceName}
          </AppText>
        </View>
        <View style={styles.meta}>
          <StatusPill
            tone={TONE[row.status]}
            size="sm"
            dot={false}
            label={t(`presence.status.${row.status}`)}
          />
          <AppText variant="mono">{formatClock(row.lastMillis)}</AppText>
        </View>
      </View>
    </Card>
  );
}

export function PresenceBoard({companyId}: {companyId: string}) {
  const {t} = useTranslation();
  const p = useTheme();
  // Свой ScrollView — значит и свой боковой отступ: страница отдаёт этому режиму всю
  // ширину, чтобы список мог уезжать под край при прокрутке.
  const insets = useContentInsets();

  // Сутки берутся один раз за монтирование: список, переехавший на новый день посреди
  // взгляда, — это список, который врёт про «сегодня» ровно до следующего обновления.
  const today = useMemo(() => isoDate(new Date()), []);

  const query = useQuery({
    queryKey: ['presence', companyId, today],
    queryFn: () =>
      fetchLogsInRange(companyId, {result: 'granted'}, dayWindow(today)),
  });

  const all = useMemo(
    () => buildPresence(query.data?.events ?? []),
    [query.data],
  );
  // Счётчики наверху считаются по ВСЕМ, а не по отобранному: они отвечают на «сколько
  // человек на объекте», и сузить их фильтром значило бы сделать из ответа тавтологию.
  const counts = presenceCounts(all);

  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  /*
    Отбор и порядок — по всему собранному дню, а не по видимой части: список
    присутствия приходит одним запросом и никуда не листается.
  */
  const needle = search.trim().toLowerCase();
  const matched = useMemo(
    () =>
      all.filter(row => {
        if (status && row.status !== status) {
          return false;
        }
        return !needle || row.name.toLowerCase().includes(needle);
      }),
    [all, status, needle],
  );

  /**
   * Порядок «по умолчанию» — это НЕ отсутствие порядка, а тот, что построил
   * `buildPresence`: сначала те, кто внутри. Он назван отдельным пунктом, потому что
   * это осмысленный ответ на «кто здесь», и вернуться к нему надо уметь.
   *
   * Ключа `default` в наборе нет намеренно: `sortRows` на неизвестный ключ отдаёт
   * копию как есть — ровно то, что нужно.
   */
  const ACCESSORS: SortAccessors<PresenceRow> = {
    name: row => row.name,
    first: row => row.firstMillis,
    last: row => row.lastMillis,
  };
  const sorted = useTableSort(matched, ACCESSORS, {key: 'default', direction: 'asc'});
  const rows = sorted.rows;
  /*
    Разбивка по страницам. «Кто сейчас на объекте» — это весь штат: на школе в тысячу
    триста человек доска рисовала тысячу триста карточек подряд, и до последней никто
    не доезжал, а до счётчиков внизу — тем более.
  */
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const paged = useClientPage(rows, pageSize);

  const filters = (
    <FilterBar testID="presence-filters">
      <FilterSelect
        label={t('presence.who')}
        value={status}
        options={[
          {value: '', label: t('actions.any')},
          {value: 'inside', label: t('presence.status.inside')},
          {value: 'seen', label: t('presence.status.seen')},
          {value: 'left', label: t('presence.status.left')},
        ]}
        onChange={setStatus}
        phone
        testID="presence-status"
      />
      {/* Варианты названы ответами, а не парой «поле + направление»: «Раньше пришли»
          понятно сразу, «Первое событие, по возрастанию» — нет. */}
      <FilterSelect
        label={t('journal.sortBy')}
        value={`${sorted.state.key}:${sorted.state.direction}`}
        options={[
          {value: 'default:asc', label: t('presence.sortDefault')},
          {value: 'name:asc', label: t('presence.sortName')},
          {value: 'first:asc', label: t('presence.sortEarliest')},
          {value: 'last:desc', label: t('presence.sortLatest')},
        ]}
        onChange={next => {
          const [key, direction] = next.split(':');
          sorted.set({key, direction: direction as 'asc' | 'desc'});
        }}
        neutral="default:asc"
        phone
        testID="presence-sort"
      />
    </FilterBar>
  );

  let content: React.ReactNode;

  if (query.isPending) {
    content = <SkeletonList count={6} />;
  } else if (query.isError) {
    content = (
      <EmptyState
        tone="error"
        title={t('presence.error')}
        actionLabel={t('actions.retry')}
        onAction={() => void query.refetch()}
      />
    );
  } else if (rows.length === 0) {
    content = (
      <EmptyState
        icon="people"
        title={t(all.length > 0 ? 'presence.emptyFiltered' : 'presence.empty')}
      />
    );
  } else {
    content = (
      <View style={styles.list}>
        {paged.rows.map(row => (
          <PresenceCard key={row.recordId} companyId={companyId} row={row} />
        ))}
        <Pager
          page={paged.page}
          totalPages={paged.totalPages}
          total={paged.total}
          perPage={paged.perPage}
          onPage={paged.setPage}
          perPageOptions={PAGE_SIZES}
          onPerPage={setPageSize}
          testID="presence-pager"
        />
      </View>
    );
  }

  // Своей прокрутки у доски нет: на экране одна полоса, и она у страницы.
  return (
    <View style={[styles.root, insets]} testID="presence-board">
      <Card padding={4}>
        <View style={styles.counts}>
          <View style={styles.count}>
            <AppText variant="eyebrow">{t('presence.status.inside')}</AppText>
            <AppText variant="metricSm" color={p.primary}>
              {formatCount(counts.inside)}
            </AppText>
          </View>
          <View style={styles.count}>
            <AppText variant="eyebrow">{t('presence.status.seen')}</AppText>
            <AppText variant="metricSm">{formatCount(counts.seen)}</AppText>
          </View>
          <View style={styles.count}>
            <AppText variant="eyebrow">{t('presence.status.left')}</AppText>
            <AppText variant="metricSm">{formatCount(counts.left)}</AppText>
          </View>
        </View>
        {/* Оговорка появляется ровно тогда, когда она правдива: пока все двери
            различают направление, счётчик «внутри» полон и объяснять нечего. */}
        {counts.seen > 0 ? <HelpNote text={t('presence.bothDoorHint')} /> : null}
      </Card>

      {query.data && !query.data.complete ? (
        <HelpNote text={t('presence.partial', {scanned: query.data.scanned})} />
      ) : null}

      <View style={styles.list}>
        <SectionHeader
          title={t('presence.title')}
          hint={t('presence.hint', {count: rows.length})}
        />
        <AppInput
          value={search}
          onChangeText={setSearch}
          placeholder={t('home.people.search')}
          icon="search"
          autoCapitalize="none"
          autoCorrect={false}
          testID="presence-search"
        />
        {filters}
        {content}
      </View>
    </View>
  );
}
